import { Link } from 'react-router-dom';
import { AuthAmbientShapes, AuthAsideContent, type AuthVisualVariant } from './AuthWellnessVisual';
import { WellnessCarousel } from '../wellness/WellnessCarousel';
import { useWellnessPlacement } from '../../hooks/useWellnessSite';
import { ThemeToggle } from '../ThemeToggle';

interface AuthShellProps {
  title: string;
  subtitle?: string;
  backTo?: string;
  backLabel?: string;
  children: React.ReactNode;
  footer?: React.ReactNode;
  progressPct?: number;
  variant?: AuthVisualVariant;
  wide?: boolean;
  brandHref?: string;
}

export function AuthShell({
  title,
  subtitle,
  backTo = '/',
  backLabel = 'Back',
  children,
  footer,
  progressPct,
  variant = 'login',
  wide = false,
  brandHref = '/',
}: AuthShellProps) {
  const isCompactAside = variant === 'login' || variant === 'recovery';
  const authSlides = useWellnessPlacement('auth');

  return (
    <div className="auth-page">
      <aside className="auth-aside">
        <div className="auth-aside-inner">
          <Link to={brandHref} className="auth-brand">
            <span className="auth-brand-mark" aria-hidden>
              ◉
            </span>
            MyLighthouse
          </Link>
          <p className="auth-aside-tagline">Licensed therapists · Confidential care</p>
          <AuthAsideContent variant={variant} compact={isCompactAside} />
          {isCompactAside && (
            <div className="auth-aside-carousel">
              <WellnessCarousel
                slides={authSlides}
                showCaptions
                compact
                className="auth-aside-carousel-inner"
              />
            </div>
          )}
        </div>
        <div className="auth-aside-glow auth-aside-glow-1" aria-hidden />
        <div className="auth-aside-glow auth-aside-glow-2" aria-hidden />
      </aside>

      <div className={`auth-main ${isCompactAside ? 'auth-main-compact' : ''}`}>
        <AuthAmbientShapes />
        <div className="auth-main-frame">
          <div className="auth-main-top">
            <Link to={backTo} className="auth-back">
              <svg
                className="h-4 w-4"
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
                aria-hidden
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M15 19l-7-7 7-7"
                />
              </svg>
              {backLabel}
            </Link>
            <Link to={brandHref} className="auth-brand-mobile">
              MyLighthouse
            </Link>
            <ThemeToggle compact />
          </div>

          <div
            className={`auth-card ${wide ? 'auth-card-wide' : ''} ${isCompactAside ? 'auth-card-compact' : ''}`}
          >
            <div className="auth-card-header">
              <h1 className="auth-title">{title}</h1>
              {subtitle && <p className="auth-subtitle">{subtitle}</p>}
              {progressPct !== undefined && (
                <div className="auth-progress">
                  <div className="auth-progress-track">
                    <div className="auth-progress-fill" style={{ width: `${progressPct}%` }} />
                  </div>
                  <span className="auth-progress-label">{progressPct}% complete</span>
                </div>
              )}
            </div>
            {children}
            {footer && <div className="auth-card-footer">{footer}</div>}
          </div>
        </div>
      </div>
    </div>
  );
}
